import './DatasetEdit.scss';

import { useCallback, useEffect, useState } from 'react'
import { Helmet } from "react-helmet";
import { Breadcrumb, Card, Container, Row } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BdaDataset } from '../../models/BdaDataset';
import { Datasets } from '../../api/Datasets';
import AppLoader from '../../components/common/AppLoader';
import { useToasts } from '../../contexts/toasts';
import { Alert } from 'antd';
import AssetEditForm from '../../components/forms/AssetEditForm';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';

export default function DatasetEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dataset, setDataset] = useState<Partial<BdaDataset>>();
  const [error, setError] = useState(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  const retrieveDataset = useCallback(() => {
    Datasets.getDataset(+id!)
      .then(
        ({ data }) => {
          setDataset({ ...data, path: data.datasetFileType!.tableName });
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasets-error', message, `Datasets Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast]);

  useEffect(() => {
    if (id) retrieveDataset();
    else setDataset(new BdaDataset());
  }, [id, retrieveDataset]);

  const onCancel = useCallback(() => navigate('/datasets'), [navigate]);

  const submitDataset = useCallback((body: BdaDataset) => {
    showLoader();

    const promise = id ? Datasets.updateDataset(body) : Datasets.createDataset(body);

    promise.then(
      ({ data }) => {
        hideLoader();
        showToast('create-dataset-success', `Dataset ${id ? 'edited' : 'registered'} successfully.`, 'Dataset Success', 'success');
        navigate(`/datasets/${data.id}`);
      },
      () => {
        hideLoader();
        showToast('create-dataset-error', `Could not ${id ? 'edit' : 'register'} dataset.`, 'Dataset Error', 'danger');
      },
    );
  }, [id, showLoader, hideLoader, navigate, showToast]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occured while retrieving dataset. Try again later."
      />
    );

    return dataset
      ? <AssetEditForm mode='dataset' asset={dataset} modeLabel='Dataset' onCancel={onCancel} onSubmit={submitDataset} />
      : (
        <div className="p-5 text-center">
          <AppLoader />
        </div>
      );
  }, [error, dataset, onCancel, submitDataset]);

  return (
    <Container className="my-3">
      <Helmet>
        <title>Dataset {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/datasets' }}>Datasets</Breadcrumb.Item>
        {id
          ? (
            <>
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/datasets/${id}` }}>{dataset?.name}</Breadcrumb.Item>
              <Breadcrumb.Item active>Edit</Breadcrumb.Item>
            </>
          )
          : (
            <Breadcrumb.Item active>New Dataset</Breadcrumb.Item>
          )}
      </Breadcrumb>

      <Card>
        <Row className="gx-0 border-bottom p-2 px-3">
          <h5 className="mb-2">{id ? 'Edit' : 'New'} Dataset</h5>
          <h6 className="text-muted mb-0">{id ? 'Edit existing dataset fields.' : 'Fill in desired dataset details.'}</h6>
        </Row>

        {renderAsyncContent()}
      </Card>
    </Container>
  )
}